import { studyQueryKeys } from '@/config/queryKeys';
import { PROTEOFORM_DEFAULT_THRESHOLD_CPM, TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import {
  PaginatedSampleProteoformsResponse,
  SampleProteoformPublic,
  StudySampleProteoformSearchRequest,
} from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { NUMBER_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';

interface TauSamplesDetailTableProps {
  orgId: string;
  studyId: string;
  assayId: string;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-tau-detail-pageSize`;
const VALID_SORT_FIELDS = ['sample_name', 'instrument_run_name', 'proteoform_name', 'proteoform_abundance'];
const DEFAULT_PAGE_SIZE = 25;
const EPITOPE_COLUMN_MIN_WIDTH = 110;
const TEXT_COLUMN_MIN_WIDTH = 200;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

// Isoform/PTM epitope columns. Each name is BOTH the header and the payload key: the response
// carries these as flat top-level booleans (SampleProteoformPublic allows extra keys), so the
// spelling must match the wire exactly — `pS202+pT205` and `pT217`, not `pS202+PT205`/`p217`.
const TAU_EPITOPE_COLUMNS: ColDef<SampleProteoformPublic>[] = [
  'N',
  '0N',
  '1N',
  '2N',
  '3R',
  '4R',
  'C',
  'pT181',
  'pS202+pT205',
  'pT205',
  'pS214',
  'pT217',
  'pT231',
  'pS396',
].map(name => ({
  colId: name,
  field: name,
  headerName: name,
  // Wide enough that sizeColumnsToFit can't shrink 17 columns into unreadable stubs; the grid
  // scrolls horizontally instead of clipping every header to a letter or two.
  minWidth: EPITOPE_COLUMN_MIN_WIDTH,
  resizable: true,
  sortable: false,
  filter: false,
  valueFormatter: ({ value }) => (typeof value === 'boolean' ? (value ? 'Y' : 'N') : ''),
}));

export const TauSamplesDetailTable = function TauSamplesDetailTable({
  orgId,
  studyId,
  assayId,
}: TauSamplesDetailTableProps) {
  const { t } = useTranslation('studies');
  const gridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<
        SampleProteoformPublic,
        PaginatedSampleProteoformsResponse,
        StudySampleProteoformSearchRequest
      >(studyQueryKeys.endpoints.proteoformsSearch(orgId, studyId), ({ limit, skip, sortBy, sortOrder, filters }) => {
        const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'sample_name';
        // The assay is scoped by the required top-level `assay_id`, not by a filter — mirrors
        // AssaySamplesTable. Omitting it is a 422.
        return {
          assay_id: assayId,
          skip,
          limit,
          sort_by: validSortBy as StudySampleProteoformSearchRequest['sort_by'],
          sort_order: (sortOrder ?? 'asc') as 'asc' | 'desc',
          threshold_cpm: PROTEOFORM_DEFAULT_THRESHOLD_CPM,
          filters: filters as StudySampleProteoformSearchRequest['filters'],
        };
      }),
    [orgId, studyId, assayId],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<SampleProteoformPublic>({
    studyId,
    tableId: 'tau-samples-detail-table',
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const colDefs: ColDef<SampleProteoformPublic>[] = useMemo(
    () => [
      {
        field: 'sample_name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        minWidth: TEXT_COLUMN_MIN_WIDTH,
        resizable: true,
        sortable: true,
      },
      {
        field: 'proteoform_name',
        headerName: t('columnHeaders.proteoformGroup'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        minWidth: TEXT_COLUMN_MIN_WIDTH,
        resizable: true,
        sortable: true,
      },
      {
        field: 'proteoform_abundance',
        headerName: t('columnHeaders.abundanceCpm'),
        filter: 'agNumberColumnFilter',
        filterParams: NUMBER_FILTER_PARAMS,
        minWidth: TEXT_COLUMN_MIN_WIDTH,
        resizable: true,
        sortable: true,
        valueFormatter: ({ value }) => value?.toLocaleString() ?? '',
      },
      ...TAU_EPITOPE_COLUMNS,
    ],
    [t],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  return (
    <Table
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      enableResponsiveResize={false}
      getRowId={(params: { data: SampleProteoformPublic }) => `${params.data.sample_id}-${params.data.proteoform_name}`}
      gridOptions={gridOptions}
      onFilterChanged={handleFilterChanged}
      onFirstDataRendered={handleFirstDataRendered}
      onGridPreDestroyed={e => flushColumnState(e.api, 'tau-samples-detail-table')}
      onGridReady={handleGridReady}
      onModelUpdated={handleModelUpdated}
      onPaginationChanged={handlePaginationChanged}
      pagination
      paginationPageSize={pageSize}
      paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
      ref={gridRef}
      resizeDebounceMs={150}
      rowModelType="serverSide"
      tableId="tau-samples-detail-table"
    />
  );
};
